/**
 * Canonical amenity chips for the partner-hotels list + modal on the
 * voucher detail page (TD-blocks 2026-07-02, Booking.com search-card
 * pattern).
 *
 * Follows the exact precedent of KEYWORD_ICON_MAP in
 * app/(marketing)/vouchers/[id]/promotion-icon.js: an ORDERED array of
 * canonical entries with a multilingual (NL/EN/DE/FR) substring keyword
 * list per entry (Kartik #sitereview #21 lesson — a Dutch-only list
 * silently fails on localized data). Raw RateHawk amenity strings NEVER
 * render; the chip label always comes from t('voucher.amenity.<key>'),
 * so every visible string is i18n-managed. Amenities that match no
 * entry are silently dropped.
 *
 * Data source: `hotel.amenity_groups` — the flattened
 * `ota_amenity_groups` rows the BFF's getHotelsForVoucherDeals endpoint
 * will emit (site-backend additive flatten; ABSENT on prod as of
 * 2026-07-02, verified via curl). Until the backend ships the field,
 * resolveAmenityChips returns [] for every hotel and the whole feature
 * is invisible — no empty chip rows, no placeholders.
 */
import {
  Wifi,
  CircleParking,
  Flower2,
  Waves,
  UtensilsCrossed,
  Dumbbell,
  Snowflake,
  PawPrint,
} from "lucide-react";

export const AMENITY_CHIP_MAP = [
  {
    key: "wifi",
    icon: Wifi,
    keywords: ["wifi", "wi-fi", "wlan", "internet"],
  },
  {
    key: "parking",
    icon: CircleParking,
    // "parkeer" (not "parkeren") so NL compounds like "parkeerplaats" /
    // "parkeergarage" match too.
    keywords: ["parking", "parkeren", "parkeer", "parkplatz"],
  },
  {
    key: "spa",
    icon: Flower2,
    keywords: ["spa", "sauna", "wellness"],
  },
  {
    key: "pool",
    icon: Waves,
    keywords: ["pool", "zwembad", "schwimmbad", "piscine"],
  },
  {
    key: "restaurant",
    icon: UtensilsCrossed,
    keywords: ["restaurant"],
  },
  {
    key: "fitness",
    icon: Dumbbell,
    keywords: ["fitness", "gym"],
  },
  {
    key: "airco",
    icon: Snowflake,
    keywords: ["airco", "air condition", "air-condition", "klimaanlage", "climatisation"],
  },
  {
    key: "pets",
    icon: PawPrint,
    keywords: ["pets", "huisdieren", "huisdier", "haustiere", "animaux"],
  },
];

/**
 * Dual-shape array parser — same defensiveness as the `hotel_id`
 * normaliser in vouchers/[id]/page.js (JV-66): real array, JSON-encoded
 * array string, comma list, scalar.
 */
function toArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string" || !raw.trim()) return [];
  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* fall through to comma / scalar handling */
    }
  }
  if (trimmed.includes(",")) {
    return trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [trimmed];
}

/**
 * Flatten a hotel's amenity_groups[].amenities entries into lowercase
 * strings. Handles the field being absent, an array, or a JSON string,
 * and amenity entries being strings or {name} objects.
 */
export function flattenHotelAmenities(hotel) {
  const groups = toArray(hotel?.amenity_groups ?? hotel?.amenityGroups);
  const out = [];
  for (const group of groups) {
    const amenities = toArray(group?.amenities);
    for (const amenity of amenities) {
      if (typeof amenity === "string" && amenity.trim()) {
        out.push(amenity.toLowerCase());
      } else if (
        amenity &&
        typeof amenity === "object" &&
        typeof amenity.name === "string" &&
        amenity.name.trim()
      ) {
        out.push(amenity.name.toLowerCase());
      }
    }
  }
  return out;
}

/**
 * Resolve a hotel's amenities to canonical chips.
 *
 * First keyword hit claims a chip; output follows AMENITY_CHIP_MAP
 * order (stable priority) regardless of the amenity order in the data.
 * Returns [] when the hotel has no amenity_groups (absent, empty, or
 * zero keyword matches) so callers can hide the chip row entirely.
 *
 * @param {object} hotel  BFF hotel record
 * @param {number} max    hard cap on returned chips (list: 6, modal: 8)
 * @returns {Array<{key: string, icon: Function}>}
 */
export function resolveAmenityChips(hotel, max = 8) {
  const amenities = flattenHotelAmenities(hotel);
  if (amenities.length === 0) return [];
  const chips = [];
  for (const entry of AMENITY_CHIP_MAP) {
    if (chips.length >= max) break;
    const matched = amenities.some((amenity) =>
      entry.keywords.some((kw) => amenity.includes(kw)),
    );
    if (matched) chips.push(entry);
  }
  return chips;
}
